'use client';

import React, { useEffect } from 'react';
import { cn } from '@/lib/utils/cn';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';

interface ToastProps {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message?: string;
  onClose: () => void;
  onClick?: () => void;
  duration?: number;
}

const iconMap = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const colorMap = {
  success: 'border-success/50 bg-success/10',
  error: 'border-destructive/50 bg-destructive/10',
  warning: 'border-warning/50 bg-warning/10',
  info: 'border-info/50 bg-info/10',
};

const iconColorMap = {
  success: 'text-success',
  error: 'text-destructive',
  warning: 'text-warning',
  info: 'text-info',
};

const roleMap = {
  success: 'status',
  error: 'alert',
  warning: 'alert',
  info: 'status',
};

export function Toast({ id, type, title, message, onClose, onClick, duration = 5000 }: ToastProps) {
  const Icon = iconMap[type];
  const toastRole = roleMap[type];

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border p-4 shadow-lg transition-all',
        colorMap[type]
      )}
      onClick={onClick}
      role={toastRole}
      aria-live="polite"
    >
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', iconColorMap[type])} />
      <div className="flex-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        {message && (
          <p className="mt-1 text-sm text-muted-foreground">{message}</p>
        )}
      </div>
      <button
        onClick={onClose}
        className="shrink-0 text-muted-foreground hover:text-foreground"
        aria-label="Close notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
Toast.displayName = "Toast";

interface ToastOptions {
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message?: string;
  duration?: number;
}

let toastContainer: HTMLDivElement | null = null;

function getToastContainer(): HTMLDivElement {
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.className = 'fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-sm';
    document.body.appendChild(toastContainer);
  }
  return toastContainer;
}

const iconPaths: Record<string, string> = {
  success: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  error: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  warning: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z',
  info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
};

export function toast(options: ToastOptions) {
  const container = getToastContainer();
  const id = crypto.randomUUID();
  const removeToast = () => {
    const el = container.querySelector(`[data-toast-id="${id}"]`);
    if (el) el.remove();
    if (container.children.length === 0) {
      container.remove();
      toastContainer = null;
    }
  };

  const toastEl = document.createElement("div");
  toastEl.setAttribute("data-toast-id", id);
  toastEl.style.animation = "slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards";

  const wrapper = document.createElement("div");
  wrapper.className = `flex items-start gap-3 rounded-lg border p-4 shadow-lg transition-all duration-200 hover:shadow-xl ${colorMap[options.type]}`;
  wrapper.setAttribute("role", roleMap[options.type]);
  wrapper.setAttribute("aria-live", "polite");

  const iconSvg = document.createElement("svg");
  iconSvg.className = `mt-0.5 h-5 w-5 shrink-0 ${iconColorMap[options.type]}`;
  iconSvg.setAttribute("fill", "none");
  iconSvg.setAttribute("stroke", "currentColor");
  iconSvg.setAttribute("viewBox", "0 0 24 24");
  iconSvg.setAttribute("aria-hidden", "true");
  const path = document.createElement("path");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  path.setAttribute("stroke-width", "2");
  path.setAttribute("d", iconPaths[options.type] || iconPaths.info);
  iconSvg.appendChild(path);

  const contentDiv = document.createElement("div");
  contentDiv.className = "flex-1";

  const titleP = document.createElement("p");
  titleP.className = "text-sm font-semibold text-foreground";
  titleP.textContent = options.title;
  contentDiv.appendChild(titleP);

  if (options.message) {
    const msgP = document.createElement("p");
    msgP.className = "mt-1 text-sm text-muted-foreground";
    msgP.textContent = options.message;
    contentDiv.appendChild(msgP);
  }

  const closeBtn = document.createElement("button");
  closeBtn.className = "shrink-0 text-muted-foreground hover:text-foreground transition-colors";
  closeBtn.setAttribute("aria-label", "Close notification");
  closeBtn.addEventListener("click", removeToast);
  const closeSvg = document.createElement("svg");
  closeSvg.className = "h-4 w-4";
  closeSvg.setAttribute("fill", "none");
  closeSvg.setAttribute("stroke", "currentColor");
  closeSvg.setAttribute("viewBox", "0 0 24 24");
  closeSvg.setAttribute("aria-hidden", "true");
  const closePath = document.createElement("path");
  closePath.setAttribute("stroke-linecap", "round");
  closePath.setAttribute("stroke-linejoin", "round");
  closePath.setAttribute("stroke-width", "2");
  closePath.setAttribute("d", "M6 18L18 6M6 6l12 12");
  closeSvg.appendChild(closePath);
  closeBtn.appendChild(closeSvg);

  wrapper.appendChild(iconSvg);
  wrapper.appendChild(contentDiv);
  wrapper.appendChild(closeBtn);
  toastEl.appendChild(wrapper);

  container.appendChild(toastEl);

  if (options.duration && options.duration > 0) {
    setTimeout(removeToast, options.duration);
  }
}
